import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Campaign, Goal, PayoutConfig, Cap, CampaignAffiliate, ApiResponse } from '../types';
import clsx from 'clsx';

type TabType = 'overview' | 'goals' | 'payouts' | 'caps' | 'affiliates';

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Campaign>>(`/campaigns/${id}`);
      return response.data.data;
    },
  });

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'goals', name: 'Goals' },
    { id: 'payouts', name: 'Payouts' },
    { id: 'caps', name: 'Caps & Limits' },
    { id: 'affiliates', name: 'Affiliates' },
  ];

  if (isLoading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (!campaign) {
    return <div className="text-center py-10">Campaign not found</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/campaigns" className="text-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
          &larr; Back to Campaigns
        </Link>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {campaign.logoUrl && (
              <img
                src={campaign.logoUrl}
                alt={campaign.name}
                className="h-16 w-16 rounded-lg object-cover"
              />
            )}
            <div>
              <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{campaign.name}</h1>
              <p className="text-sm text-[var(--text-muted)]">{campaign.slug}</p>
            </div>
          </div>
          <StatusBadge status={campaign.status} />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border-color)] mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={clsx(
                'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab campaign={campaign} />}
      {activeTab === 'goals' && <GoalsTab campaignId={id!} />}
      {activeTab === 'payouts' && <PayoutsTab campaignId={id!} />}
      {activeTab === 'caps' && <CapsTab campaignId={id!} />}
      {activeTab === 'affiliates' && <AffiliatesTab campaignId={id!} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
    active: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800',
    disabled: 'bg-red-100 text-red-800',
    archived: 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]',
  };

  return (
    <span className={clsx('badge', colors[status] || 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]')}>
      {status}
    </span>
  );
}

function OverviewTab({ campaign }: { campaign: Campaign }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Campaign Details</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Category</dt>
            <dd className="text-[var(--text-primary)]">{campaign.category}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Visibility</dt>
            <dd className="text-[var(--text-primary)]">{campaign.visibility}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Currency</dt>
            <dd className="text-[var(--text-primary)]">{campaign.currency}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Cookie Lifetime</dt>
            <dd className="text-[var(--text-primary)]">{campaign.cookieLifetimeDays} days</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Attribution Model</dt>
            <dd className="text-[var(--text-primary)]">{campaign.attributionModel.replace('_', ' ')}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Featured</dt>
            <dd className="text-[var(--text-primary)]">{campaign.featured ? 'Yes' : 'No'}</dd>
          </div>
        </dl>
      </div>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Statistics</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Goals</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign._count?.goals || 0}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Affiliates</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign._count?.campaignAffiliates || 0}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Clicks</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign._count?.clicks || 0}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-[var(--text-muted)]">Conversions</dt>
            <dd className="text-[var(--text-primary)] font-medium">{campaign._count?.conversions || 0}</dd>
          </div>
        </dl>
      </div>

      {campaign.description && (
        <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6 md:col-span-2">
          <h3 className="text-lg font-medium mb-4">Description</h3>
          <p className="text-[var(--text-secondary)]">{campaign.description}</p>
        </div>
      )}

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg p-6 md:col-span-2">
        <h3 className="text-lg font-medium mb-4">Tracking URL</h3>
        <code className="block bg-[var(--bg-tertiary)] p-3 rounded text-sm break-all">
          {campaign.trackingUrl}
        </code>
      </div>
    </div>
  );
}

function GoalsTab({ campaignId }: { campaignId: string }) {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: goals, isLoading } = useQuery({
    queryKey: ['goals', campaignId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Goal[]>>(`/goals?campaignId=${campaignId}`);
      return response.data.data;
    },
  });

  const createGoal = useMutation({
    mutationFn: async (data: Partial<Goal>) => {
      await api.post('/goals', { ...data, campaignId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', campaignId] });
      toast.success('Goal created');
      setShowModal(false);
    },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Goals</h3>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          Add Goal
        </button>
      </div>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">Name</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Value</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Default</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Conversions</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr><td colSpan={5} className="py-10 text-center">Loading...</td></tr>
            ) : goals?.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">No goals yet</td></tr>
            ) : (
              goals?.map((goal) => (
                <tr key={goal.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-[var(--text-primary)]">{goal.name}</td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    <code className="bg-[var(--bg-tertiary)] px-2 py-1 rounded">{goal.value}</code>
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {goal.isDefault ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">{goal._count?.conversions || 0}</td>
                  <td className="px-3 py-4 text-sm">
                    <span className={clsx('badge', goal.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]')}>
                      {goal.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <GoalModal
          onClose={() => setShowModal(false)}
          onSave={(data) => createGoal.mutate(data)}
          isLoading={createGoal.isPending}
        />
      )}
    </div>
  );
}

function GoalModal({
  onClose,
  onSave,
  isLoading,
}: {
  onClose: () => void;
  onSave: (data: Partial<Goal>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    description: '',
    isDefault: false,
    requiresAmount: true,
    isUniquePerPlayer: false,
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-[var(--bg-secondary)]0 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-[var(--bg-secondary)] rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-medium mb-4">Add Goal</h3>
          <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input mt-1"
                placeholder="e.g., First Deposit"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Value (Event Type)</label>
              <select
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="input mt-1"
              >
                <option value="">Select...</option>
                <option value="registration">Registration</option>
                <option value="ftd">First Deposit (FTD)</option>
                <option value="deposit">Deposit</option>
                <option value="qualified">Qualified Player</option>
                <option value="purchase">Purchase</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input mt-1"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <span className="text-sm">Default Goal</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.requiresAmount}
                  onChange={(e) => setFormData({ ...formData, requiresAmount: e.target.checked })}
                />
                <span className="text-sm">Requires Amount</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? 'Saving...' : 'Save Goal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PayoutsTab({ campaignId }: { campaignId: string }) {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: payouts, isLoading } = useQuery({
    queryKey: ['payout-configs', campaignId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<PayoutConfig[]>>(`/payout-configs?campaignId=${campaignId}`);
      return response.data.data;
    },
  });

  const createPayout = useMutation({
    mutationFn: async (data: Partial<PayoutConfig>) => {
      await api.post('/payout-configs', { ...data, campaignId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payout-configs', campaignId] });
      toast.success('Payout config created');
      setShowModal(false);
    },
  });

  const paymentTypeLabels: Record<string, string> = {
    cpa: 'CPA',
    cps: 'CPS',
    revshare: 'RevShare',
    hybrid: 'Hybrid',
    cpl: 'CPL',
    cpi: 'CPI',
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Payout Configurations</h3>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          Add Payout
        </button>
      </div>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">Title</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Type</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Goal</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Amount</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr><td colSpan={5} className="py-10 text-center">Loading...</td></tr>
            ) : payouts?.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-[var(--text-muted)]">No payout configs yet</td></tr>
            ) : (
              payouts?.map((payout) => (
                <tr key={payout.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-[var(--text-primary)]">
                    {payout.title}
                    {payout.type === 'personal' && (
                      <span className="ml-2 badge bg-purple-100 text-purple-800">Personal</span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {paymentTypeLabels[payout.paymentType] || payout.paymentType}
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {payout.goal?.name || payout.goalValue || '-'}
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {payout.amountType === 'percentage'
                      ? `${payout.amount}%`
                      : `${payout.currency} ${payout.amount}`}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <span className={clsx('badge', payout.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]')}>
                      {payout.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <PayoutModal
          onClose={() => setShowModal(false)}
          onSave={(data) => createPayout.mutate(data)}
          isLoading={createPayout.isPending}
        />
      )}
    </div>
  );
}

function PayoutModal({
  onClose,
  onSave,
  isLoading,
}: {
  onClose: () => void;
  onSave: (data: Partial<PayoutConfig>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<{
    title: string;
    type: 'general' | 'personal';
    paymentType: 'cpa' | 'cps' | 'revshare' | 'hybrid' | 'cpl' | 'cpi';
    goalValue: string;
    amount: number;
    amountType: 'fixed' | 'percentage';
    currency: string;
  }>({
    title: '',
    type: 'general',
    paymentType: 'cpa',
    goalValue: '',
    amount: 0,
    amountType: 'fixed',
    currency: 'USD',
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-[var(--bg-secondary)]0 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-[var(--bg-secondary)] rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-medium mb-4">Add Payout Configuration</h3>
          <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Payment Type</label>
                <select
                  value={formData.paymentType}
                  onChange={(e) => setFormData({ ...formData, paymentType: e.target.value as typeof formData.paymentType })}
                  className="input mt-1"
                >
                  <option value="cpa">CPA</option>
                  <option value="cps">CPS</option>
                  <option value="revshare">RevShare</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="cpl">CPL</option>
                  <option value="cpi">CPI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Amount Type</label>
                <select
                  value={formData.amountType}
                  onChange={(e) => setFormData({ ...formData, amountType: e.target.value as typeof formData.amountType })}
                  className="input mt-1"
                >
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  className="input mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="input mt-1"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Goal Value (Event)</label>
              <select
                value={formData.goalValue}
                onChange={(e) => setFormData({ ...formData, goalValue: e.target.value })}
                className="input mt-1"
              >
                <option value="">Any Goal</option>
                <option value="registration">Registration</option>
                <option value="ftd">First Deposit</option>
                <option value="deposit">Deposit</option>
                <option value="qualified">Qualified</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? 'Saving...' : 'Save Payout'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CapsTab({ campaignId }: { campaignId: string }) {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: caps, isLoading } = useQuery({
    queryKey: ['caps', campaignId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Cap[]>>(`/caps?campaignId=${campaignId}`);
      return response.data.data;
    },
  });

  const createCap = useMutation({
    mutationFn: async (data: Partial<Cap>) => {
      await api.post('/caps', { ...data, campaignId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caps', campaignId] });
      toast.success('Cap created');
      setShowModal(false);
    },
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Caps & Limits</h3>
        <button onClick={() => setShowModal(true)} className="btn btn-primary">
          Add Cap
        </button>
      </div>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">Title</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Type</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Limit</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Period</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Usage</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr><td colSpan={6} className="py-10 text-center">Loading...</td></tr>
            ) : caps?.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">No caps configured</td></tr>
            ) : (
              caps?.map((cap) => (
                <tr key={cap.id}>
                  <td className="py-4 pl-4 pr-3 text-sm font-medium text-[var(--text-primary)]">{cap.title}</td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">{cap.capType}</td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">{cap.value}</td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">{cap.period}</td>
                  <td className="px-3 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className={clsx(
                            'h-2 rounded-full',
                            parseFloat(cap.percentageUsed || '0') >= 90 ? 'bg-red-500' :
                            parseFloat(cap.percentageUsed || '0') >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                          )}
                          style={{ width: `${Math.min(100, parseFloat(cap.percentageUsed || '0'))}%` }}
                        />
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">{cap.currentCount}/{cap.value}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <span className={clsx('badge', cap.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]')}>
                      {cap.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <CapModal
          onClose={() => setShowModal(false)}
          onSave={(data) => createCap.mutate(data)}
          isLoading={createCap.isPending}
        />
      )}
    </div>
  );
}

function CapModal({
  onClose,
  onSave,
  isLoading,
}: {
  onClose: () => void;
  onSave: (data: Partial<Cap>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<{
    title: string;
    capType: 'conversions' | 'leads' | 'commission_sum' | 'clicks';
    value: number;
    period: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'total';
    actionOnCap: string;
  }>({
    title: '',
    capType: 'conversions',
    value: 100,
    period: 'daily',
    actionOnCap: 'reject',
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-[var(--bg-secondary)]0 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-[var(--bg-secondary)] rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-medium mb-4">Add Cap</h3>
          <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Cap Type</label>
                <select
                  value={formData.capType}
                  onChange={(e) => setFormData({ ...formData, capType: e.target.value as typeof formData.capType })}
                  className="input mt-1"
                >
                  <option value="conversions">Conversions</option>
                  <option value="leads">Leads</option>
                  <option value="commission_sum">Commission Sum</option>
                  <option value="clicks">Clicks</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Period</label>
                <select
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value as typeof formData.period })}
                  className="input mt-1"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="total">Total</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Limit Value</label>
                <input
                  type="number"
                  required
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
                  className="input mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">Action on Cap</label>
                <select
                  value={formData.actionOnCap}
                  onChange={(e) => setFormData({ ...formData, actionOnCap: e.target.value })}
                  className="input mt-1"
                >
                  <option value="reject">Reject</option>
                  <option value="hold">Hold</option>
                  <option value="redirect">Redirect</option>
                  <option value="notify">Notify Only</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? 'Saving...' : 'Save Cap'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AffiliatesTab({ campaignId }: { campaignId: string }) {
  const queryClient = useQueryClient();

  const { data: affiliates, isLoading } = useQuery({
    queryKey: ['campaign-affiliates', campaignId],
    queryFn: async () => {
      const response = await api.get<ApiResponse<CampaignAffiliate[]>>(`/campaigns/${campaignId}/affiliates`);
      return response.data.data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ affiliateId, status }: { affiliateId: string; status: string }) => {
      await api.patch(`/campaigns/${campaignId}/affiliates/${affiliateId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-affiliates', campaignId] });
      toast.success('Status updated');
    },
  });

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    suspended: 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]',
  };

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Campaign Affiliates</h3>

      <div className="bg-[var(--bg-secondary)] shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-[var(--bg-secondary)]">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-[var(--text-primary)]">Affiliate</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Referral Code</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Applied</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-[var(--text-primary)]">Payout Group</th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-[var(--bg-secondary)]">
            {isLoading ? (
              <tr><td colSpan={6} className="py-10 text-center">Loading...</td></tr>
            ) : affiliates?.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-[var(--text-muted)]">No affiliates in this campaign</td></tr>
            ) : (
              affiliates?.map((ca) => (
                <tr key={ca.id}>
                  <td className="py-4 pl-4 pr-3 text-sm">
                    <div className="font-medium text-[var(--text-primary)]">{ca.affiliate?.name}</div>
                    <div className="text-[var(--text-muted)]">{ca.affiliate?.email}</div>
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <code className="bg-[var(--bg-tertiary)] px-2 py-1 rounded">{ca.affiliate?.referralCode}</code>
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {new Date(ca.appliedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <span className={clsx('badge', statusColors[ca.status] || 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]')}>
                      {ca.status}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-[var(--text-muted)]">
                    {ca.payoutGroup?.name || 'Default'}
                  </td>
                  <td className="py-4 pl-3 pr-4 text-right text-sm">
                    {ca.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus.mutate({ affiliateId: ca.affiliateId, status: 'approved' })}
                          className="text-green-600 hover:text-green-900 mr-2"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateStatus.mutate({ affiliateId: ca.affiliateId, status: 'rejected' })}
                          className="text-red-600 hover:text-red-900"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {ca.status === 'approved' && (
                      <button
                        onClick={() => updateStatus.mutate({ affiliateId: ca.affiliateId, status: 'suspended' })}
                        className="text-red-600 hover:text-red-900"
                      >
                        Suspend
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
